import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { UserPlus, Calendar, Users, Clock, ArrowRight, Sparkles } from 'lucide-react';
import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';

export function LandingPage() {
  const navigate = useNavigate();
  const [userName, setUserName] = useState<string>('');

  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.email && window.location.pathname !== '/auth/login') {
        setUserName(user.email.split('@')[0]);
      } else {
        setUserName('');
      }
    };
    getUser();
    
    const { data: authListener } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        setUserName('');
      }
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  const initiateSession = () => {
    const sessionId = uuidv4();
    navigate(`/new/session?id=${sessionId}`);
  };

  const features = [
    {
      icon: <Calendar className="w-8 h-8 text-[#ffcc32]" />,
      title: "Smart Calendar Integration",
      description: "Seamlessly connect with Google Calendar to manage availability and schedule sessions."
    },
    {
      icon: <Users className="w-8 h-8 text-[#ffcc32]" />,
      title: "Mentor-Mentee Matching",
      description: "Efficiently pair mentors with mentees based on availability and preferences."
    },
    {
      icon: <Clock className="w-8 h-8 text-[#ffcc32]" />,
      title: "Automated Scheduling",
      description: "Let mentees choose from available time slots without back-and-forth communication."
    }
  ];

  return (
    <div className="max-w-6xl mx-auto">
      <div className="bg-white border rounded-xl p-6 mb-16 flex items-center gap-6">
        <div className="bg-amber-50 p-4 rounded-lg">
          <Sparkles className="w-8 h-8 text-[#ffcc32]" />
        </div>
        <div>
          <h2 className="text-2xl font-semibold mb-2">
            Welcome back{userName ? `, ${userName}` : ''}! 👋
          </h2>
          <p className="text-gray-600">
            Ready to make some meaningful connections today? Start a new mentorship session
            or manage your existing ones.
          </p>
        </div>
      </div>

      <div className="text-center mb-16">
        <h1 className="text-5xl font-bold mb-6 bg-gradient-to-r from-[#ffcc32] to-amber-500 bg-clip-text text-transparent">
          VINCER Coordinator Dashboard
        </h1>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
          Streamline your mentorship program with our powerful coordination tools.
          Connect mentors and mentees effortlessly.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-8 mb-16">
        {features.map((feature, index) => (
          <div key={index} className="bg-white p-8 rounded-xl shadow-sm border hover:shadow-md transition-shadow">
            <div className="bg-amber-50 w-16 h-16 rounded-lg flex items-center justify-center mb-6">
              {feature.icon}
            </div>
            <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
            <p className="text-gray-600">{feature.description}</p>
          </div>
        ))}
      </div>

      <div className="bg-gradient-to-br from-amber-50 to-white p-12 rounded-2xl shadow-sm border">
        <div className="max-w-3xl mx-auto text-center">
          <UserPlus className="w-20 h-20 text-[#ffcc32] mx-auto mb-8" />
          <h2 className="text-3xl font-bold mb-6">Create New Mentorship Session</h2>
          <p className="text-gray-600 text-lg mb-8">
            Ready to make a connection? Start a new mentorship session and facilitate
            meaningful relationships between mentors and mentees.
          </p>
          <button
            onClick={initiateSession}
            className="group bg-[#ffcc32] text-black font-medium px-8 py-4 rounded-lg hover:bg-[#ffd65c] transition-all text-lg inline-flex items-center gap-3"
          >
            Start New Session
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    </div>
  );
}